import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadState, LoadStateInfo, loaded, loading, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { ObjectTarget } from "../../app/permissions";
import { StudentInfo } from "../student/studentSlice";
import { SchoolInfo } from "../school/schoolSlice";
import { ClassroomInfo } from "../classroom/classroomSlice";
import { UserInfo } from "../user/userSlice";
import { NamedObject } from "./namedObject";
import { DptoInfo } from "../dpto/dptoSlice";
import { RoutineInfo } from "../routine/routineSlice";
import { SubjectInfo } from "../curriculum/model";

export interface ObjectCacheState {
    // key = objectTarget:targetId
    requests: Record<string, LoadStateInfo>;
    cache: {
        schools: Record<string, SchoolInfo>;
        classrooms: Record<string, ClassroomInfo>;
        students: Record<string, StudentInfo>;
        users: Record<string, UserInfo>;
        dptos: Record<string, DptoInfo>;
        routines: Record<string, RoutineInfo>;
        subjects: Record<string, SubjectInfo>;
    }
    error: {
        schools: Record<string, string>;
        classrooms: Record<string, string>;
        students: Record<string, string>;
        users: Record<string, string>;
        dptos: Record<string, string>;
        routines: Record<string, string>;
        subjects: Record<string, string>;
    }
}

const initialState: ObjectCacheState = {
    requests: {},
    cache: {
        schools: {},
        classrooms: {},
        students: {},
        users: {},
        dptos: {},
        routines: {},
        subjects: {},
    },
    error: {
        schools: {},
        classrooms: {},
        students: {},
        users: {},
        dptos: {},
        routines: {},
        subjects: {},
    }
};

export interface ObjectRetrieveRequest {
    objectTarget: ObjectTarget;
    targetId: string;
}

export const retrieveObject = createAsyncThunk(
    "retrieveObject",
    async (param: ObjectRetrieveRequest, thunkAPI) => {
        // console.debug(`retrieve ${JSON.stringify(param)}`);
        let endpoint = undefined;
        switch (param.objectTarget) {
            case ObjectTarget.SCHOOL: endpoint = "school"; break;
            case ObjectTarget.CLASSROOM: endpoint = "classroom"; break;
            case ObjectTarget.STUDENT: endpoint = "student"; break;
            case ObjectTarget.USER: endpoint = "user"; break;
            case ObjectTarget.DPTO: endpoint = "dpto"; break;
            case ObjectTarget.ROUTINE: endpoint = "routine"; break;
            case ObjectTarget.SUBJECT: endpoint = "subject"; break;
            default: throw new Error(`Unknown object target: ${param.objectTarget}`);
        }
        const response = await fetch(`${getBackendUrl()}/api/${endpoint}/${param.targetId}`, {
            method: "GET", signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: ObjectRetrieveRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.objectCache.requests[param.objectTarget + ":" + param.targetId];
            if (status && (status.state == LoadState.Loading || status.state == LoadState.Failed)) {
                return false; // Do not proceed if already loading
            }
            return true; // Proceed with the request
        }
    }
);

export function getOrRetrieveNamedObject(dispatch: any, state: ObjectCacheState, target?: ObjectTarget, id?: string): NamedObject | undefined {
    if (!target || !id) {
        return undefined;
    }
    const req: ObjectRetrieveRequest = { objectTarget: target, targetId: id };
    const namedObject = getNamedObject(state, req);
    if (namedObject) {
        return namedObject;
    } else {
        dispatch( retrieveObject(req) );
        return undefined;
    }
}

export function getNamedObjectBy(state: ObjectCacheState, target?: ObjectTarget, id?: string): NamedObject | undefined {
    if (!target || !id) {
        return undefined;
    }
    const req: ObjectRetrieveRequest = { objectTarget: target, targetId: id };
    return getNamedObject(state, req);
}

export function getNamedObject(state: ObjectCacheState, req: ObjectRetrieveRequest): NamedObject | undefined {
    const type = req.objectTarget;
    const id = req.targetId;
    let ret = undefined as NamedObject | undefined;
    switch (type) {
        case ObjectTarget.SCHOOL: {
            const school = state.cache.schools[id];
            if (school) {
                ret = NamedObject.school(school);
            }
            break;
        }
        case ObjectTarget.CLASSROOM: {
            const classroom = state.cache.classrooms[id];
            if (classroom) {
                ret = NamedObject.classroom(classroom);
            }
            break;
        }
        case ObjectTarget.STUDENT: {
            const student = state.cache.students[id];
            if (student) {
                ret = NamedObject.student(student);
            }
            break;
        }
        case ObjectTarget.USER: {
            const user = state.cache.users[id];
            if (user) {
                ret = NamedObject.user(user);
            }
            break;
        }
        case ObjectTarget.DPTO: {
            const dpto = state.cache.dptos[id];
            if (dpto) {
                ret = NamedObject.dpto(dpto);
            }
            break;
        }
        case ObjectTarget.ROUTINE: {
            const routine = state.cache.routines[id];
            if (routine) {
                ret = NamedObject.routine(routine);
            }
            break;
        }
        case ObjectTarget.SUBJECT: {
            const subject = state.cache.subjects[id];
            if (subject) {
                ret = NamedObject.subject(subject);
            }
            break;
        }
        default: {
            throw new Error(`Unknown object type: ${type}`);
        }
    }
    return ret;
}

export const objectCacheSlice = createSlice({
    name: "objectCache",
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveObject.pending, (state, action) => {
                // console.debug(`${action.type} for ${action.meta.arg.objectTarget}:${action.meta.arg.targetId}`);
                state.requests[action.meta.arg.objectTarget + ":" + action.meta.arg.targetId] = loading();
            })
            .addCase(retrieveObject.fulfilled, (state, action) => {
                // console.debug(`${action.type} for ${action.meta.arg.objectTarget}:${action.meta.arg.targetId}`);
                switch (action.meta.arg.objectTarget) {
                    case ObjectTarget.SCHOOL:
                        state.cache.schools[action.meta.arg.targetId] = action.payload;
                        break;
                    case ObjectTarget.CLASSROOM:
                        state.cache.classrooms[action.meta.arg.targetId] = action.payload;
                        break;
                    case ObjectTarget.STUDENT:
                        state.cache.students[action.meta.arg.targetId] = action.payload;
                        break;
                    case ObjectTarget.USER:
                        state.cache.users[action.meta.arg.targetId] = action.payload;
                        break;
                    case ObjectTarget.DPTO:
                        state.cache.dptos[action.meta.arg.targetId] = action.payload;
                        break;
                    case ObjectTarget.ROUTINE:
                        state.cache.routines[action.meta.arg.targetId] = action.payload;
                        break;
                    case ObjectTarget.SUBJECT:
                        state.cache.subjects[action.meta.arg.targetId] = action.payload;
                        break;
                }
                state.requests[action.meta.arg.objectTarget + ":" + action.meta.arg.targetId] = loaded();
            })
            .addCase(retrieveObject.rejected, (state, action) => {
                // console.debug(`${action.type} for ${action.meta.arg.objectTarget}:${action.meta.arg.targetId}`);
                switch (action.meta.arg.objectTarget) {
                    case ObjectTarget.SCHOOL:
                        state.error.schools[action.meta.arg.targetId] = action.error.message || "Error";
                        break;
                    case ObjectTarget.CLASSROOM:
                        state.error.classrooms[action.meta.arg.targetId] = action.error.message || "Error";
                        break;
                    case ObjectTarget.STUDENT:
                        state.error.students[action.meta.arg.targetId] = action.error.message || "Error";
                        break;
                    case ObjectTarget.USER:
                        state.error.users[action.meta.arg.targetId] = action.error.message || "Error";
                        break;
                    case ObjectTarget.DPTO:
                        state.error.dptos[action.meta.arg.targetId] = action.error.message || "Error";
                        break;
                    case ObjectTarget.ROUTINE:
                        state.error.routines[action.meta.arg.targetId] = action.error.message || "Error";
                        break;
                    case ObjectTarget.SUBJECT:
                        state.error.subjects[action.meta.arg.targetId] = action.error.message || "Error";
                        break;
                }
                state.requests[action.meta.arg.objectTarget + ":" + action.meta.arg.targetId] = error(action.error.message || `Error retrieving object ${action.meta.arg.objectTarget}:${action.meta.arg.targetId}`);
            });
    },
});

// export const { } = objectCacheSlice.actions;

export const selectObjectCache = (state: RootState) => state.objectCache;

export default objectCacheSlice.reducer;
